import type { GetVatInformations } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import type { I18n } from '@backmarket/nuxt-module-i18n/types'
import {
  FORM_VALID,
  type Validator,
  type ValidatorDeclaration,
  matchingRegExp,
  required,
} from '@ds/components/Form'

import { ALLOWED_FILES_COUNT } from '../../../../LegalDetails.constants'
import { LEGAL_AREAS } from '../../TaxDetails.constants'

import {
  MIN_VAT_NUMBERS_BY_COUNTRY,
  REGEXP_VALID_VAT_NUMBER,
} from './TaxDetailsModal.constants'
import translations from './TaxDetailsModal.translations'
import type { FormTaxKey, FormTaxValues } from './TaxDetailsModal.types'

export function shouldUseVatScheme(values: FormTaxValues) {
  return (
    (values.legalArea === LEGAL_AREAS.EU && !values.isExemptFromVat) ||
    (values.legalArea === LEGAL_AREAS.NON_EU && values.hasEstablishmentInEu)
  )
}

export function shouldUseOss({
  vatMarginScheme,
  isExemptFromVat,
}: FormTaxValues) {
  return (
    (
      ['NORMAL_VAT', 'MIXED_VAT'] as GetVatInformations['vatMarginScheme'][]
    ).includes(vatMarginScheme as GetVatInformations['vatMarginScheme']) &&
    !isExemptFromVat
  )
}

// The taxDetailsModal form being dynamic, we need to validate only the displayed inputs
// This helper returns only the validation keys related to the user choices
export function getFormValidationKeys(values: FormTaxValues) {
  // Common keys
  const keys: FormTaxKey[] = ['legalArea', 'expeditionCountry']
  // NON-EU form
  if (values.legalArea === LEGAL_AREAS.NON_EU) {
    keys.push('taxIdNumber')
    keys.push('hasEstablishmentInEu')
    if (values.hasEstablishmentInEu) keys.push('intraCommunityVatNumber')
  }
  // EU Form / establishment
  if (shouldUseVatScheme(values)) {
    keys.push('intraCommunityVatNumber')
    keys.push('vatMarginScheme')
    if (shouldUseOss(values)) {
      keys.push('isOssRegistered')
      if (values.isOssRegistered) {
        keys.push('ossDocuments')
      } else {
        keys.push('vatNumbersByCountry')
      }
    }
  }

  return keys
}

export function getFormValidationRules(
  i18n: I18n,
  values: FormTaxValues,
): ValidatorDeclaration<FormTaxValues> {
  if (!values.hasCompletedVatInfos) {
    return {}
  }
  const rules: Record<FormTaxKey, Validator<FormTaxValues>[]> = {
    legalArea: [required(i18n(translations.legalAreaRequired))],
    expeditionCountry: [required(i18n(translations.expeditionCountryRequired))],
    taxIdNumber: [required(i18n(translations.taxIdRequired))],
    hasEstablishmentInEu: [
      required(i18n(translations.euEstablishmentRequired)),
    ],
    intraCommunityVatNumber: [
      required(i18n(translations.vatNumberRequired)),
      matchingRegExp(
        REGEXP_VALID_VAT_NUMBER,
        i18n(translations.vatNumberInvalidFormat),
      ),
    ],
    vatMarginScheme: [required(i18n(translations.vatMarginSchemeRequired))],
    isOssRegistered: [required(i18n(translations.ossRegistrationRequired))],
    ossDocuments: [
      ((
        ossDocuments: FormTaxValues['ossDocuments'],
        formValues: FormTaxValues,
      ) => {
        return ossDocuments.length === ALLOWED_FILES_COUNT ||
          formValues.files.length === ALLOWED_FILES_COUNT
          ? FORM_VALID
          : i18n(translations.ossDocumentRequired)
      }) as Validator<FormTaxValues>,
    ],
    vatNumbersByCountry: [
      ((vatNumbersByCountry: FormTaxValues['vatNumbersByCountry']) => {
        return vatNumbersByCountry &&
          vatNumbersByCountry.length >= MIN_VAT_NUMBERS_BY_COUNTRY
          ? FORM_VALID
          : i18n(translations.vatNumbersByCountryRequired)
      }) as Validator<FormTaxValues>,
    ],
    isExemptFromVat: [],
    files: [],
    hasCompletedVatInfos: [],
  }
  const usedKeys = getFormValidationKeys(values)

  return Object.fromEntries(
    (Object.entries(rules) as [FormTaxKey, never][]).filter(([key]) =>
      usedKeys.includes(key),
    ),
  )
}

export function cleanFormValues(formValues: FormTaxValues) {
  const usedKeys = getFormValidationKeys(formValues)
  // For each field we only keep the fields corresponding to the client path
  // We erase any locally saved fields from another path through the form
  const values: Partial<FormTaxValues> = (
    Object.keys(formValues) as FormTaxKey[]
  ).reduce((keys, key: FormTaxKey) => {
    const defaultValue = typeof formValues[key] === 'string' ? '' : null

    return {
      ...keys,
      [key]: usedKeys.includes(key) ? formValues[key] : defaultValue,
    }
  }, {})
  // hasCompletedVatInfos is an hidden fields, not corresponding to a UI input
  // so it won't appear in the usedKeys, we need to add it manually after cleaning
  values.hasCompletedVatInfos = formValues.hasCompletedVatInfos

  // Same logic here with isExemptFromVat that is not depending on a validation rules
  if (formValues.isExemptFromVat && formValues.legalArea === LEGAL_AREAS.EU) {
    values.isExemptFromVat = formValues.isExemptFromVat
  }
  // The files attribute is only used locally to temporary host uploaded file before API upload call
  // We need to remove it from the PUT request body
  delete values.files

  return values
}
